import React, { PropsWithChildren, useCallback, useState } from "react";

import VisibilityOn from "public/icons/visibility.svg";
import VisibilityOff from "public/icons/visibility_off.svg";

import {
  IconButton,
  InputAdornment,
  TextFieldProps,
  TextField as MuiTextField,
  styled,
  css,
} from "@mui/material";

import { SvgIcon } from "@/components/ui/svg-icon/SvgIcon";
import { ConditionalWrapper } from "@/shared/ConditionalWrapper";
import { ThemeProviderWithPrimaryPink } from "@/shared/ThemeProviderWithPrimaryPink";
import { skipForwardProp } from "@/utils/skipForwardProp";

type PropTypes = {
  pinkVersion?: boolean;
};

const StyledTextField = styled(MuiTextField, {
  shouldForwardProp: skipForwardProp(["pinkVersion"]),
})<PropTypes>(({ pinkVersion, theme }) => {
  return css`
    .MuiOutlinedInput-root {
      height: 40px;
    }

    input {
      height: 40px;
      box-sizing: border-box;
    }

    label:not(.Mui-focused):not(.MuiInputLabel-shrink) {
      top: -5px;
    }

    ${pinkVersion
      ? css`
          label.MuiFormLabel-filled ~ div > fieldset {
            border-color: ${theme.palette.primary.main} !important;
            border-width: 2px;
            border-radius: 3px;
          }

          label.MuiFormLabel-filled,
          label.Mui-focused {
            color: ${theme.palette.primary.main} !important;
          }
        `
      : ""}
  `;
});

export const TextField = React.forwardRef<
  HTMLInputElement,
  TextFieldProps & PropsWithChildren & PropTypes
>(({ children, type, pinkVersion = false, ...rest }, ref) => {
  const [showPassword, setShowPassword] = useState(false);

  const inputType =
    type !== "password" ? type : showPassword ? "text" : "password";

  const PasswordAdornment = useCallback(() => {
    return (
      <InputAdornment position="end">
        <IconButton
          aria-label="toggle password visibility"
          onClick={() => setShowPassword((state) => !state)}
          edge="end"
        >
          {showPassword ? (
            <SvgIcon fontSize="small" color="grey">
              <VisibilityOn />
            </SvgIcon>
          ) : (
            <SvgIcon fontSize="small" color="grey">
              <VisibilityOff />
            </SvgIcon>
          )}
        </IconButton>
      </InputAdornment>
    );
  }, [showPassword]);

  const InputPasswordProps: TextFieldProps["InputProps"] =
    type === "password"
      ? {
          endAdornment: <PasswordAdornment />,
        }
      : {};

  return (
    <ConditionalWrapper
      on={pinkVersion}
      Wrapper={({ children }) => {
        return (
          <ThemeProviderWithPrimaryPink>
            {children}
          </ThemeProviderWithPrimaryPink>
        );
      }}
    >
      <StyledTextField
        pinkVersion={pinkVersion}
        inputRef={ref}
        autoComplete={"false"}
        fullWidth
        InputProps={InputPasswordProps}
        type={inputType}
        {...rest}
      >
        {children}
      </StyledTextField>
    </ConditionalWrapper>
  );
});

TextField.displayName = "TextField";
